import { useEffect, useRef } from "react";

interface PollingConfig<T = any> {
  interval: number; // Initial interval (ms)
  maxInterval?: number; // Optional max polling interval(ms)
  onPoll: () => Promise<T>; // Function to execute at each poll
  onSuccess?: (result: T) => void; // Optional success callback
  dependencies?: any[]; // Dependency list to conditionally trigger polling
  dependencyCondition?: boolean; // Condition to trigger polling
}

export const usePolling = <T = any>({
  interval,
  maxInterval,
  onPoll,
  onSuccess,
  dependencies = [],
  dependencyCondition = true,
}: PollingConfig<T>) => {
  const elapsed = useRef(0);
  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const currentInterval = useRef(interval);

  const poll = async () => {
    try {
      const result = await onPoll();
      onSuccess?.(result);
    } catch (error) {
      console.error("Polling error:", error);
    }

    elapsed.current += currentInterval.current;

    if (!maxInterval || elapsed.current < maxInterval) {
      currentInterval.current = Math.min(
        currentInterval.current * 2,
        maxInterval
          ? maxInterval - elapsed.current
          : currentInterval.current * 2
      );
    }
    timeoutId.current = setTimeout(poll, currentInterval.current);
  };

  useEffect(() => {
    if (!dependencyCondition) return;

    poll();

    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, [...dependencies, dependencyCondition]);
};
